/**
 * @license Copyright (c) Microsoft Corporation.  All rights reserved.
 */
import { Logger } from "./Logger";
import { doesPhoneNumberStartWithCountryCode, findCheckboxes, getParentElement, findParentFieldBlock } from "./Utils/HtmlUtils";
import { LookupDataValueAttribute } from "./Lookups/LookupView";
import { HtmlAttributes } from "../../../Common/Constants/HtmlAttributes";
import { WaitlistOptions } from "./EventManagement/Constants";
var FormSerializer = /** @class */ (function () {
    function FormSerializer() {
    }
    FormSerializer.serialize = function (form, builder) {
        if (!form) {
            this.logger.error("form was null");
            return null;
        }
        var elements = form.elements;
        for (var i = 0; i < elements.length; i++) {
            var currentElement = elements[i];
            var name_1 = currentElement["name"];
            this.serializeElement(currentElement, name_1, builder);
        }
    };
    FormSerializer.serializeElement = function (currentElement, name, builder) {
        if (!name) {
            return;
        }
        if (this.isIgnoredField(currentElement)) {
            this.logger.log("ignored element ".concat(name));
            return;
        }
        switch (currentElement.nodeName) {
            case "INPUT":
                this.serializeInput(currentElement, name, builder);
                break;
            case "TEXTAREA":
                this.serializeTextArea(currentElement, name, builder);
                break;
            case "BUTTON":
                this.serializeButton(currentElement, name, builder);
                break;
            case "SELECT":
                this.serializeSelect(currentElement, name, builder);
                break;
            case "FIELDSET":
                this.serializeFieldSet(currentElement, name, builder);
                break;
            default:
                this.logger.log("ignored element ".concat(name, " with unknown type: ").concat(typeof currentElement));
                break;
        }
    };
    FormSerializer.updateLabels = function (field, name, valueLabel, builder) {
        var _a, _b, _c;
        var block = findParentFieldBlock(field);
        if (!valueLabel && block) {
            try {
                var labels = JSON.parse(block === null || block === void 0 ? void 0 : block.getAttribute((_a = HtmlAttributes.optionsValues) !== null && _a !== void 0 ? _a : "[]"));
                valueLabel = (_b = labels.filter(function (l) { return l.value == field.value; })[0]) === null || _b === void 0 ? void 0 : _b.label;
            }
            catch (_d) {
                // Ignore parse issue
            }
        }
        builder.updateLabels(name, Array.from((_c = field.labels) !== null && _c !== void 0 ? _c : [], function (l) { return l.innerText; }), valueLabel, block === null || block === void 0 ? void 0 : block.getAttribute(HtmlAttributes.savedFieldId));
    };
    FormSerializer.serializeInput = function (input, name, builder) {
        var _a, _b, _c, _d, _e, _f, _g;
        if (input.type === "file") {
            return;
        }
        if (input.type === "datetime-local") {
            if (input.value) {
                var parsedDate = Date.parse(input.value.replace("T", " ").replace(/-/g, "/"));
                var utcDateTime = isNaN(parsedDate) ? input.value : new Date(parsedDate).toISOString();
                builder.add(name, utcDateTime);
            }
            else {
                builder.add(name, input.value);
            }
            this.updateLabels(input, name, null, builder);
            return;
        }
        if (input.type === "radio" || input.type === "checkbox") {
            if (input.type === "checkbox") {
                var parent = getParentElement(input, function (element) { return element.nodeName === "FIELDSET"; });
                if (parent && parent["name"] === input["name"]) {
                    // multi-select checkbox scenario
                    return;
                }
            }
            // Consent block
            if (input.type === "checkbox" &&
                (name.startsWith("msdynmkt_purposeid") || name.startsWith("msdynmkt_consenttypevalue") || name.startsWith("msdynmkt_topicid"))) {
                var labelElement = (_a = input.parentElement) === null || _a === void 0 ? void 0 : _a.querySelector("label[for=\"".concat(input.id, "\"]"));
                var labelText = (_c = (_b = labelElement === null || labelElement === void 0 ? void 0 : labelElement.firstChild) === null || _b === void 0 ? void 0 : _b.textContent) !== null && _c !== void 0 ? _c : "";
                var query = name.startsWith("msdynmkt_topicid") ? '[data-editorblocktype="Topic"]' : '[data-editorblocktype="Consent"]';
                var complianceId = (_e = (_d = input.closest(query)) === null || _d === void 0 ? void 0 : _d.getAttribute("data-compliancesettingsid")) !== null && _e !== void 0 ? _e : "";
                var values = input.value.split(";");
                var purposeId = name.startsWith("msdynmkt_purposeid") ? values[0] : null;
                var topicId = name.startsWith("msdynmkt_topicid") ? values[0] : null;
                var consentTypeValue = name.startsWith("msdynmkt_consenttypevalue") ? values[0] : null;
                var jsonFieldName = "consentsubmissionvalues";
                var fieldValue = {
                    msdynmkt_purposeid: purposeId !== null && purposeId !== void 0 ? purposeId : null,
                    msdynmkt_topicid: topicId !== null && topicId !== void 0 ? topicId : null,
                    msdynmkt_consenttypevalue: consentTypeValue !== null && consentTypeValue !== void 0 ? consentTypeValue : null,
                    channels: (_f = values[1]) !== null && _f !== void 0 ? _f : null,
                    optinwhenchecked: (_g = values[2]) !== null && _g !== void 0 ? _g : null,
                    msdynmkt_value: input.checked ? "Opted In" : "Opted Out",
                    labelText: labelText,
                    msdynmkt_compliancesettings4id: complianceId
                };
                builder.add(jsonFieldName, JSON.stringify(fieldValue));
                this.updateLabels(input, name, null, builder);
                return;
            }
            // Event sessions block
            if (["checkbox", "radio"].includes(input.type) && name.startsWith("msevtmgt_session")) {
                var id = input.getAttribute("id");
                var sessionId = id.slice(0, id.indexOf("_"));
                var fieldKey = input.type === "radio" ? "".concat(name, ":").concat(sessionId) : name;
                if (input.checked) {
                    var sessionElement = input.closest("div.eventSession");
                    var capacity = sessionElement === null || sessionElement === void 0 ? void 0 : sessionElement.getAttribute(HtmlAttributes.dataAvailableSeats);
                    var waitlistStatus = sessionElement === null || sessionElement === void 0 ? void 0 : sessionElement.getAttribute(HtmlAttributes.dataWaitlistEnabled);
                    var isSessionWaitlisted = parseInt(capacity) <= 0 && waitlistStatus === WaitlistOptions.Yes;
                    var value = isSessionWaitlisted ? "waitlist" : input.value;
                    builder.add(fieldKey, value);
                }
                else {
                    builder.add(fieldKey, "");
                }
                this.updateLabels(input, name, null, builder);
                return;
            }
            if (input.type === "radio") {
                var radioValue = builder.get(name);
                if (radioValue === undefined) {
                    builder.add(name, input.checked ? input.value : "");
                    this.updateLabels(input, name, null, builder);
                    return;
                }
                if (radioValue.value === "") {
                    builder.update(name, input.checked ? input.value : "");
                    this.updateLabels(input, name, null, builder);
                }
                return;
            }
            if (input.type === "checkbox") {
                input.checked ? builder.add(name, input.value) : builder.add(name, "");
                this.updateLabels(input, name, null, builder);
            }
            return;
        }
        var lookupAttributeValue = input.getAttribute(LookupDataValueAttribute);
        if (lookupAttributeValue && lookupAttributeValue.length) {
            var parts = lookupAttributeValue.split(",");
            builder.add(name, parts[1], parts[0]);
            this.updateLabels(input, name, null, builder);
            return;
        }
        if (input.type === "tel" && this.serializePhoneField(input, name, builder)) {
            return;
        }
        builder.add(name, input.value);
        this.updateLabels(input, name, null, builder);
    };
    FormSerializer.shouldAppendCountryCode = function (countryCode, inputValue) {
        return inputValue && countryCode && countryCode !== "+" && !doesPhoneNumberStartWithCountryCode(inputValue);
    };
    FormSerializer.serializeTextArea = function (input, name, builder) {
        builder.add(name, input.value);
        this.updateLabels(input, name, null, builder);
    };
    FormSerializer.serializeButton = function (input, name, builder) {
        builder.add(name, input.value);
    };
    FormSerializer.serializeSelect = function (input, name, builder) {
        var _a, _b;
        if (input.type === "select-one") {
            builder.add(name, input.value);
            this.updateLabels(input, name, (_b = (_a = input.selectedOptions) === null || _a === void 0 ? void 0 : _a[0]) === null || _b === void 0 ? void 0 : _b.label, builder);
        }
    };
    FormSerializer.serializeFieldSet = function (fieldSet, name, builder) {
        var checkboxes = findCheckboxes(fieldSet);
        var selected = Array.from(checkboxes !== null && checkboxes !== void 0 ? checkboxes : [])
            .filter(function (checkbox) { return checkbox.checked; });
        if (checkboxes === null || checkboxes === void 0 ? void 0 : checkboxes.length) {
            var checkboxesSerialized = JSON.stringify(selected.map(function (checkbox) { return checkbox.value; }));
            builder.add(name, checkboxesSerialized);
            this.updateLabels(fieldSet, name, JSON.stringify(selected.map(function (c) {
                var _a;
                return ({
                    value: c.value,
                    label: c.labels && ((_a = c.labels[0]) === null || _a === void 0 ? void 0 : _a.innerText)
                });
            })), builder);
        }
    };
    FormSerializer.isIgnoredField = function (element) {
        return element.getAttribute(this.ignoredFieldAttrName) === "true";
    };
    FormSerializer.serializePhoneField = function (input, name, builder) {
        var _a;
        var divBlockElement = (_a = input.parentElement) === null || _a === void 0 ? void 0 : _a.parentElement;
        var isInsidePhoneEditorBlock = divBlockElement &&
            divBlockElement.nodeType === Node.ELEMENT_NODE &&
            divBlockElement.nodeName.toLowerCase() === "div" &&
            divBlockElement.getAttribute(HtmlAttributes.blockType) === "PhoneFormField";
        var hasCountryCodePresent = isInsidePhoneEditorBlock && (divBlockElement === null || divBlockElement === void 0 ? void 0 : divBlockElement.hasAttribute(HtmlAttributes.dataCountryCode));
        if (hasCountryCodePresent) {
            var countryCode = divBlockElement.getAttribute(HtmlAttributes.dataCountryCode);
            builder.add(name, this.shouldAppendCountryCode(countryCode, input.value) ? countryCode + input.value : input.value);
            return true;
        }
        return false;
    };
    FormSerializer.ignoredFieldAttrName = "data-field-ignored";
    FormSerializer.logger = new Logger();
    return FormSerializer;
}());
export { FormSerializer };
